import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import styled, { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'

import TextInput, { ErrorMessage } from 'components/TextInput'
import IconButton from 'components/Buttons/IconButton'

import { FixMeAny } from 'types/global'
import { colors } from 'utils/colors'

import variants from './variants'
import Avatar from '../Avatar'
import Icon from '../Icon'
import Text from '../Text'

import { IndicatorsContainer } from './custom'

import {
  DropdownContainer,
  LabelText,
  MultiValueContainer,
  RequiredText,
  StyledControl,
  StyledIcon,
  StyledInput,
  StyledOption,
  StyledOptionWithAvatar,
} from './styles'

import {
  ControlProps,
  DropdownItem,
  DropdownProps,
  MenuProps,
  MultiValueProps,
  OptionProps,
  SingleValueProps,
  ValueType,
  VariantType,
} from './types'
import Box from 'components/Box'
import Flex from 'components/Flex'
import RadioBox from 'components/RadioBox'
import InlineInputButtons from 'components/Buttons/InlineInputButtons/v2'
import useOnClickOutside from 'hooks/use-click-outside-hooks'
import InlineEditBtn from 'modules/Leads/LeadDetailV2/shared/InlineEditBtn'

const Dropdown = <T extends string | number, IsMulti extends boolean = false>({
  autoFocus = false,
  options,
  defaultValue,
  placeholder = 'please_select_lbl',
  variant = 'simple',
  width = 'auto',
  error,
  inline,
  isDisabled,
  customComponents,
  actionClick,
  label,
  marginBottom,
  marginLeft,
  marginRight,
  marginTop,
  mb,
  mr,
  mt,
  my,
  isMenuOpenByDefault,
  selectValueContainerColor,
  onChange,
  onBlur,
  onToggleMenu,
  showError = true,
  hideIndicator = false,
  multiValueChip,
  isCreatable = false,
  menuWidth,
  placeholderColor,
  withNoneOption = false,
  value,
  hideDisabledOption = true,
  usePortal = false,
  maxChipLimit,
  defaultIndention,
  styles,
  displayNode,
  saveOnClickOutside = true,
  ...props
}: DropdownProps<T, IsMulti>) => {
  const theme = useContext(ThemeContext)
  const ref = useRef<HTMLInputElement>(null)
  const [internalValue, setInternalValue] = useState<ValueType<DropdownItem<T> | DropdownItem<T>[]>>(null)
  const variantStyles = variants[variant as VariantType]
  const [menuOpen, setMenuOpen] = useState<boolean | undefined>(props.menuOpen)
  const [subMenuOpen, setSubMenuOpen] = useState<{ [key: string]: boolean }>({})
  const selectRestProps = { ...props, hideIndicator }

  const [isEditBtnDisplay, setEditBtnDisplay] = useState(false)
  const [isEditClicked, setEditClicked] = useState(false)

  const { t } = useTranslation()

  useOnClickOutside(ref, () => {
    if (!isEditClicked) {
      return
    }

    if (hasValueChanged) {
      if (saveOnClickOutside) {
        handleSelectChange(internalValue)
      } else {
        setInternalValue(selectedValue)
      }
    }

    setEditBtnDisplay(false)
    setEditClicked(false)
  })

  const getOptions = useCallback(
    (
      options: DropdownItem<T | null>[],
      defaultValue: DropdownProps<T>['defaultValue'],
      withNoneOption: DropdownProps<T>['withNoneOption'],
    ): DropdownItem<T | null>[] => {
      if (!options) return options

      let opts: DropdownItem<T | null>[] = options.filter((o) => o.type !== 'action')
      let hasGroup = false

      opts = opts
        .filter((o) => !(hideDisabledOption && Boolean(o.isDisabled)))
        .map((opt) => {
          if (!opt.options) return opt

          hasGroup = true
          return {
            ...opt,
            options: getOptions(opt.options, defaultValue, withNoneOption).map((o) => ({
              ...o,
              indention: defaultIndention ? o.indention ?? 1 : 0,
            })),
          }
        })

      if (!hasGroup && opts && defaultValue) {
        opts = opts.filter((option) => {
          if (option.type === 'action') {
            return false
          }

          if (Array.isArray(defaultValue)) {
            let keep = true

            defaultValue.forEach((o) => {
              if (keep) keep = o.value !== option.value
            })

            return keep
          } else {
            return option.value !== defaultValue?.value
          }
        })

        if (Array.isArray(defaultValue)) {
          defaultValue.forEach((d) => {
            if (options.find((o) => o.value === d.value)) {
              opts.unshift(d as DropdownItem<T>)
            }
          })
        } else if (options.find((o) => o.value === defaultValue.value)) {
          opts.unshift(defaultValue as DropdownItem<T>)
        }
      }

      const actionOptions: DropdownItem<T | null>[] = options
        .filter((o) => o.type === 'action')
        .map((o) => ({ ...o, indention: hasGroup ? 2 : undefined }))

      if (withNoneOption) {
        const noneOption: DropdownItem<T> = {
          label: 'none_with_dash_lbl',
          value: '' as T,
        }
        return [noneOption, ...opts.filter((i) => i.value !== ''), ...actionOptions].map((o) => ({
          ...o,
          label: t(o.label),
        }))
      }

      return [...opts, ...actionOptions].map((o) => ({ ...o, label: t(o.label) }))
    },
    [hideDisabledOption, t],
  )

  const cleanOptions = useMemo(() => getOptions(options, defaultValue, withNoneOption), [
    defaultValue,
    getOptions,
    options,
    withNoneOption,
  ])

  const selectedValue = useMemo(() => {
    if (value && !Array.isArray(value) && value?.value !== '' && !cleanOptions.find((o) => o.value === value?.value)) {
      return value
    }
    return getValue(cleanOptions, value)
  }, [cleanOptions, value])

  const hasValueChanged = useMemo(() => {
    const vals = Array.isArray(internalValue) ? internalValue : [internalValue]
    const values = Array.isArray(selectedValue) ? selectedValue : [selectedValue]
    return vals.length !== values.length || vals.some((val) => !values.find((sel) => sel?.value === val?.value))
  }, [internalValue, selectedValue])

  useEffect(() => {
    setInternalValue(selectedValue)
  }, [selectedValue])

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.({ ...e, target: { ...e.target, name: props.name } })
  }

  const Control: React.FC<ControlProps<DropdownItem<T>>> = useCallback(
    ({ children, ...props }) => {
      const currentValue = value || (!Array.isArray(defaultValue) && defaultValue)
      if (currentValue && !Array.isArray(currentValue) && currentValue?.avatarPlaceHolder) {
        return (
          <StyledControl
            containerColor={selectValueContainerColor && colors[`${selectValueContainerColor}`]}
            {...props}
          >
            <Avatar
              size="xsmall"
              inline
              imgSrc={currentValue?.avatar}
              mr="8px"
              placeholder={currentValue?.avatarPlaceHolder}
            />
            {children}
          </StyledControl>
        )
      }

      return (
        <StyledControl {...props} containerColor={selectValueContainerColor && colors[`${selectValueContainerColor}`]}>
          {children}
        </StyledControl>
      )
    },
    [defaultValue, selectValueContainerColor, value],
  )

  const Menu: React.FC<MenuProps<DropdownItem<T>>> = useCallback(
    (props) => {
      const Component = customComponents?.Menu ? customComponents.Menu : components.Menu
      return <Component {...props} />
    },
    [customComponents?.Menu],
  )

  const handleSubMenuOpen = (id: string[]) => {
    id.forEach((i) => {
      if (subMenuOpen[i] === undefined) {
        setSubMenuOpen((prev) => ({
          ...prev,
          [i]: true,
        }))
      } else {
        setSubMenuOpen((prev) => ({
          ...prev,
          [i]: !prev[i],
        }))
      }
    })
  }

  const LimitedChipsContainer = useCallback(({ children, ...props }) => {
    const [chips, otherChildren] = children
    const defaultContainer = () => <components.ValueContainer {...props}>{children}</components.ValueContainer>
    if (!maxChipLimit) {
      return defaultContainer()
    }
    if (typeof chips === 'object' && !Array.isArray(chips)) {
      return defaultContainer()
    }
    if (chips.length <= maxChipLimit) {
      return defaultContainer()
    }

    const displayChips = chips.slice(0, maxChipLimit)
    const overflowCount = chips.length - maxChipLimit

    return (
      <components.ValueContainer {...props} isMaxLimitReached={true}>
        {displayChips}
        {overflowCount > 0 && <span>+{overflowCount}</span>}
        {otherChildren}
      </components.ValueContainer>
    )
  }, [])

  const MultiValue: React.FC<MultiValueProps<DropdownItem<T>>> = useCallback(
    (props) => <MultiValueContainer disabled={isDisabled}>{props.data.label}</MultiValueContainer>,
    [isDisabled],
  )

  const Input: React.FC<FixMeAny> = useCallback(
    ({ children, ...props }) => <StyledInput {...props}>{children}</StyledInput>,
    [],
  )

  const Option: React.FC<OptionProps<DropdownItem<T>>> = useCallback(
    (props) => {
      if (customComponents?.Option) return <customComponents.Option {...props} />

      const isNone =
        (typeof props.data.value === 'string' && props.data.value.trim() === '') ||
        (Array.isArray(props.data.value) && props.data.value.find((i: DropdownItem<T>) => i.value === ''))

      const getLabelColor = () => {
        if (props.data.color) {
          return props.data.color
        }

        if (isNone) {
          return 'disabledText'
        }

        return 'defaultText'
      }

      const labelProps = {
        color: getLabelColor(),
        backgroundColor: 'gray1',
        inline: true,
        isCapitalizedFirstLetter: true,
      }

      if (props.data?.type === 'action') {
        return (
          <StyledOption {...props} isFixedWoIcon={props.data?.isFixed && !props.data?.icon}>
            <Box
              alignItems="center"
              display="flex"
              gap="8px"
              onClick={
                actionClick
                  ? (e) => {
                      e.stopPropagation()
                      actionClick(props.data?.value)
                      setMenuOpen(false)
                    }
                  : undefined
              }
            >
              {props.data?.icon && <StyledIcon color="blue1" fontSize={16} icon={props.data.icon} />}
              <Text
                fontWeight={variant === 'action' ? 'bold' : props.data?.type === 'action' ? 'medium' : 'normal'}
                color={variant === 'action' ? 'gray7' : props.data?.color ?? 'blue1'}
                inline
                isCapitalizedFirstLetter
              >
                {props.data.label}
              </Text>
            </Box>
          </StyledOption>
        )
      }

      if (props.isMulti) {
        const isShowingSubMenu =
          typeof subMenuOpen[props.data.value] !== undefined ? subMenuOpen[props.data.value] : false

        return props.data?.type === 'radio' ? (
          <>
            {isShowingSubMenu && (
              <components.Option {...props}>
                <Flex justifyContent="space-between" alignItems="center" width={'100%'}>
                  <Flex alignItems="center" width={'100%'} pt={10} pb={10}>
                    <RadioBox
                      key={props.data?.value}
                      selected={props.isSelected}
                      disabled={false}
                      onChange={() => props.setValue(props.data?.value)}
                      error={error}
                      ml={55}
                    />
                    <Text {...labelProps}>{props.data?.label}</Text>
                  </Flex>
                  {props.data?.icon && (
                    <StyledIcon color={props.data.iconColor ?? 'blue1'} fontSize={16} icon={props.data.icon} />
                  )}
                </Flex>
              </components.Option>
            )}
          </>
        ) : (
          <>
            <components.Option {...props}>
              <Flex justifyContent="space-between" alignItems="center" width={'100%'}>
                <Flex gap="16px" alignItems="center">
                  <StyledIcon
                    color={props.isSelected ? 'blue1' : 'disabledText'}
                    fontSize={18}
                    icon={props.isSelected ? ['fas', 'check-square'] : ['far', 'square']}
                    indention={props.data?.indention}
                  />
                  <Text {...labelProps}>{props.data?.label}</Text>
                </Flex>
                {props.data?.icon && (
                  <StyledIcon color={props.data.iconColor ?? 'blue1'} fontSize={16} icon={props.data.icon} />
                )}
                {props.data?.isCollapsible && (
                  <IconButton
                    key={props.data?.value}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSubMenuOpen([props.data.value, ...props.data.options.map((i: any) => i.id)])
                    }}
                    icon={isShowingSubMenu ? ['fas', 'chevron-up'] : ['fas', 'chevron-down']}
                    variant="ghost"
                  />
                )}
              </Flex>
            </components.Option>
          </>
        )
      }

      if (props.data.avatarPlaceHolder) {
        return (
          <StyledOptionWithAvatar {...props} indention={0} className="option-with-avatar">
            <Box alignItems="center" display="flex" gap="8px">
              <Avatar size="xsmall" inline imgSrc={props.data.avatar} placeholder={props.data.avatarPlaceHolder} />
              <Text {...labelProps}>{props.data.label}</Text>
            </Box>
            {props.isSelected && <Icon icon="check" color="blue1" fontSize={14} mr="8px" />}
          </StyledOptionWithAvatar>
        )
      }

      if (props.data.node) {
        return (
          <StyledOption {...props} indention={props.data?.indention}>
            {props.data.node}
          </StyledOption>
        )
      }

      return (
        <StyledOption {...props} indention={props.data?.indention}>
          <Box alignItems="center" display="flex" gap="8px">
            {props.data?.icon && (
              <Icon color={props.data?.iconColor ?? props.data.color ?? 'blue1'} icon={props.data.icon} />
            )}
            <Box display="flex" flexDirection="column">
              <Text {...labelProps} lineHeight={props.data.subText ? '22px' : undefined}>
                {props.data.label}
              </Text>
              {props.data.subText && (
                <Text color={props.data.subTextColor || 'gray5'} fontSize={12} lineHeight="14px">
                  {props.data.subText}
                </Text>
              )}
            </Box>
          </Box>
          {props.isSelected && <Icon icon="check" color="blue1" fontSize={14} mr="8px" />}
        </StyledOption>
      )
    },
    [actionClick, customComponents?.Option, variant, subMenuOpen],
  )

  const SingleValue: React.FC<SingleValueProps<DropdownItem<T>>> = useCallback(
    ({ children, ...props }) => {
      if (!customComponents?.SingleValue && variant === 'single-boxless') {
        return <components.SingleValue {...props}>{children}</components.SingleValue>
      }

      const Component = customComponents?.SingleValue ? customComponents.SingleValue : components.SingleValue

      return (
        <Component {...props}>
          {props.data.icon && <Icon color={props.data?.iconColor ?? 'blue1'} icon={props.data.icon} mr="16px" />}
          {children}
        </Component>
      )
    },
    [customComponents?.SingleValue, variant],
  )

  const handleMenuOpen = () => {
    if (menuOpen !== undefined) setMenuOpen(undefined)
  }

  const marginProps = {
    marginBottom,
    marginLeft,
    marginRight,
    marginTop,
    mb,
    mr,
    mt,
    my,
  }

  const containerProps = {
    ...marginProps,
    disabled: isDisabled,
    inline,
    width,
  }

  const isNoneSelected = (selection: ValueType<DropdownItem<T> | null | Array<DropdownItem<T> | null>>): boolean => {
    if (Array.isArray(selection)) {
      return Boolean(selection.find((i: DropdownItem<T> | null) => String(i?.value) === ''))
    }
    return String(selection?.value) === ''
  }

  const handleSelectChange = (recentSelectedValue: ValueType<DropdownItem<T> | DropdownItem<T>[]>) => {
    if (props.isMulti && Array.isArray(recentSelectedValue)) {
      // Current value has None and other option is selected, unselect None option
      if (isNoneSelected(value) && isNoneSelected(recentSelectedValue) && recentSelectedValue.length > 1) {
        return onChange?.(recentSelectedValue.filter((i) => String(i.value) !== '') as ValueType<DropdownItem<T>[]>)
      }

      // Current value don't have None, but new value has None selected. Return None only
      if (!isNoneSelected(value) && isNoneSelected(recentSelectedValue)) {
        const noneOption = cleanOptions.find((i) => String(i.value) === '')
        return onChange?.(noneOption ? [noneOption] : ([] as ValueType<DropdownItem<T>[]>))
      }
    }
    onChange?.(recentSelectedValue)
  }

  useEffect(() => {
    if (onToggleMenu) {
      onToggleMenu(Boolean(menuOpen))
    }
  }, [menuOpen, onToggleMenu])

  const formatGroupLabel = (group: FixMeAny) => (
    <Flex padding={'4px 0'} justifyContent={'space-between'}>
      <Text color="gray5" fontSize="14px" textTransform="uppercase" fontWeight="medium">
        {group.label}
      </Text>
      <Text fontSize="14px" padding={0}>
        {group.totalSelected ? `${group.totalSelected} selected` : ''}
      </Text>
    </Flex>
  )

  const selectComponents = useMemo(
    () => ({
      Control,
      IndicatorsContainer,
      Input,
      Menu,
      MultiValue: multiValueChip ?? MultiValue,
      Option,
      SingleValue,
      ValueContainer: LimitedChipsContainer,
    }),
    [multiValueChip, Control, Input, Menu, MultiValue, Option, SingleValue, LimitedChipsContainer],
  )
  const placeholderText = useMemo(() => {
    if (isDisabled && (value === null || value === undefined)) {
      return '-'
    }
    return t(placeholder)
  }, [isDisabled, value, t, placeholder])

  if (props.readOnly) {
    const val = Array.isArray(selectedValue)
      ? selectedValue.map((i) => i !== undefined && i.label).join(' | ')
      : selectedValue?.label
    if (displayNode) {
      return <>{displayNode}</>
    }
    return (
      <TextInput
        {...marginProps}
        label={label}
        name={props.name}
        readOnly
        required={props.required}
        value={val}
        variant={variant === 'labeled' ? 'labeled' : 'simple'}
      />
    )
  }

  if (displayNode && !isEditClicked) {
    return (
      <FieldBoxWrapper
        position="relative"
        onClick={() => setEditClicked(true)}
        onMouseEnter={() => setEditBtnDisplay(true)}
        onMouseLeave={() => setEditBtnDisplay(false)}
      >
        {displayNode}
        {isEditBtnDisplay && !isDisabled && (
          <Box transform="translateY(-50%)" position="absolute" right={0} top="50%" backgroundColor="white">
            <InlineEditBtn />
          </Box>
        )}
      </FieldBoxWrapper>
    )
  }

  return (
    <DropdownContainer {...containerProps} isEditClicked={isEditClicked} ref={ref}>
      {variant === 'labeled' && (
        <>
          <LabelText disabled={isDisabled} inline>
            {label}
          </LabelText>
          {props.required && <RequiredText>*</RequiredText>}
        </>
      )}
      <Flex justifyContent="space-between" gap="8px" height="40px">
        <Box flexGrow={1}>
          {isCreatable ? (
            <CreatableSelect
              options={cleanOptions}
              defaultValue={defaultValue}
              placeholder={placeholderText}
              formatGroupLabel={formatGroupLabel}
              components={selectComponents}
              menuPortalTarget={usePortal ? document.body : undefined}
              styles={{
                ...variantStyles({ inline, variant, width, theme, error }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                placeholder: (base) => ({
                  ...base,
                  ...(placeholderColor && { color: colors[placeholderColor], padding: '10px' }),
                }),
                ...styles,
              }}
              hideSelectedOptions={false}
              isDisabled={isDisabled}
              closeMenuOnSelect={!props.isMulti}
              menuIsOpen={menuOpen}
              defaultMenuIsOpen={isMenuOpenByDefault}
              menuPlacement="auto"
              autoFocus={autoFocus}
              onMenuOpen={handleMenuOpen}
              onChange={setInternalValue}
              value={selectedValue}
              onBlur={handleBlur}
              classNamePrefix={'react-select'}
              menuShouldBlockScroll
              {...selectRestProps}
            />
          ) : (
            <Select
              options={cleanOptions}
              components={selectComponents}
              formatGroupLabel={formatGroupLabel}
              menuPortalTarget={usePortal ? document.body : undefined}
              styles={{
                ...variantStyles({ inline, variant, width, theme, error }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({ ...base, minHeight: '40px', padding: '0px 16px' }),
                menu: (base) => ({ ...base, ...(menuWidth && { width: menuWidth }) }),
                placeholder: (base) => ({
                  ...base,
                  ...(placeholderColor && { color: colors[placeholderColor], padding: '10px' }),
                }),
                ...styles,
              }}
              hideSelectedOptions={false}
              isDisabled={isDisabled}
              closeMenuOnSelect={!props.isMulti}
              menuIsOpen={menuOpen}
              defaultMenuIsOpen={isMenuOpenByDefault}
              menuPlacement="auto"
              autoFocus={autoFocus}
              onMenuOpen={handleMenuOpen}
              value={internalValue}
              onChange={setInternalValue}
              onBlur={handleBlur}
              classNamePrefix={'react-select'}
              placeholder={placeholderText}
              menuShouldBlockScroll
              {...selectRestProps}
            />
          )}
        </Box>

        {displayNode && (
          <Flex justifyContent="flex-end" alignItems="flex-end" gap="0">
            <InlineInputButtons
              position="relative"
              // customCancel={cancelButton}
              // customConfirm={confirmButton}
              isConfirmDisabled={!hasValueChanged}
              onClickCancel={() => {
                setInternalValue(selectedValue) // reset to prop value on cancel

                setEditBtnDisplay(false)
                setEditClicked(false)
              }}
              onClickConfirm={() => {
                handleSelectChange(internalValue)
                setEditBtnDisplay(false)
                setEditClicked(false)
                // handleConfirmationClick()
                // setIsEditing(false)
              }}
            />
          </Flex>
        )}
      </Flex>
      {error && showError && <ErrorMessage>{error}</ErrorMessage>}
    </DropdownContainer>
  )
}

const getValue = <T,>(options: DropdownItem<T>[], selected?: DropdownItem<T> | DropdownItem<T>[] | null) => {
  if (
    !selected ||
    (!Array.isArray(selected) && !selected.value && typeof selected.value === 'string' && selected.value.trim() !== '')
  ) {
    return null
  }

  if (Array.isArray(selected)) {
    const multiSelectedValuesList = selected.map((i) => i?.value)

    if (isGroupOptions(options)) {
      const flatGroupOptions = options
        .flatMap((item) => item.options)
        .filter((i) => i !== undefined && i.value !== undefined)

      return flatGroupOptions.filter((i) => i !== undefined && multiSelectedValuesList.includes(i.value))
    }

    return options.filter((i) => multiSelectedValuesList.includes(i.value))
  }

  return options.find((i) => i.value === selected.value) || selected
}

const isGroupOptions = (options: DropdownItem<FixMeAny>[]) => {
  const arr = options.filter((opt) => (opt.options || []).length > 0)
  if (arr.length > 0) {
    return arr.every((obj) => obj.hasOwnProperty('options'))
  }
  return false
}

const FieldBoxWrapper = styled(Box)`
  *:hover {
    background: ${({ theme }) => theme.colors.gray1};
  }
`

export type { DropdownProps }

export default Dropdown
